/**
 * Recently-fulfilled social-proof strip for the pricing-page 4th column.
 *
 * Renders directly below ServicesCard. Pulls the last 7-day (auto-extends to 30d)
 * count of paid `service_orders` from `/api/services/social-proof` and shows a
 * pulse-dot + headline + per-service breakdown chips. Hidden when total=0 so the
 * launch period stays clean.
 */
import { useEffect, useState } from "react";
import { Flame, CheckCircle2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServicesSocialProofStrip() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/services/social-proof`).then((r) => {
      if (!cancelled) setData(r.data);
    }).catch(() => { /* silent — keep strip hidden on error */ });
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.total || data.total < 1) return null;

  const { total, window_days, breakdown, latest_service } = data;
  const noun = total === 1 ? "wholesaler" : "wholesalers";
  const headline = latest_service
    ? `${total} ${noun} booked ${latest_service}${total > 1 ? ` & more` : ""}`
    : `${total} ${noun} just bought a money-maker service`;

  return (
    <div
      className="mt-8 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-emerald-50 to-teal-50 px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-5"
      data-testid="services-social-proof-strip"
    >
      <div className="flex items-center gap-2 shrink-0">
        <span className="relative inline-flex">
          <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
        </span>
        <Flame size={15} className="text-amber-500" />
        <span className="text-[11px] uppercase tracking-widest font-bold text-emerald-800">
          Recently fulfilled · last {window_days} days
        </span>
      </div>
      <div className="flex-1 text-sm md:text-base font-semibold text-emerald-900">
        {headline}
      </div>
      {breakdown && breakdown.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {breakdown.slice(0, 4).map((b) => (
            <span
              key={b.service}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-[11px] text-emerald-800 font-medium"
              data-testid={`services-social-proof-chip-${b.service.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <CheckCircle2 size={11} className="text-emerald-500" />
              {b.service} · {b.count}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
